import type { LiteralUnion } from 'type-fest';
import { useTranslation } from 'react-i18next';
import {
  type CategoryDisplayType,
  categoryDisplaySchema,
} from '@/consts/backend-i18n-derived';
import { findRecursive } from '@/lib/recursive-helper';
import {
  type CategoryData,
  useQueryCategories,
} from '@/services/use-query-categories';

type DisplayName = LiteralUnion<CategoryDisplayType, string>;
type CategoryI18N = Pick<CategoryData, 'displayName' | 'code'>;

export const displayNameToIdBase = (
  data: CategoryData[],
  displayName: DisplayName,
) => {
  const parsedDisplayName = categoryDisplaySchema.safeParse(displayName);

  if (parsedDisplayName.error) {
    throw new Error(`Can't parse displayName: ${displayName}`);
  }

  const target = findRecursive(
    data,
    (item) => item.displayName === parsedDisplayName.data,
  );

  if (!target) {
    throw new Error(`Can't get item ${displayName}`);
  }

  return target.id;
};

export const useCategoryDataTransform = () => {
  const { t } = useTranslation(['backend-i18n']);

  const { data, isLoading, isFetched } = useQueryCategories({
    networkMode: 'offlineFirst',
  });
  const isReady = !isLoading && isFetched && !!data;

  const formatCategoryTitle = ({ code, displayName }: CategoryI18N) => {
    const parsedDisplayName = categoryDisplaySchema.safeParse(displayName);
    const title = parsedDisplayName.success
      ? `${code}-${t(`backend-i18n:${parsedDisplayName.data}`)}`
      : `${code}-${displayName}`;

    return title;
  };

  const displayNameToTitle = (displayName: DisplayName) => {
    if (!data) {
      return;
    }

    const target = findRecursive(
      data,
      (item) => item.displayName === displayName,
    );

    if (!target) {
      throw new Error(`Can't get item ${displayName}`);
    }

    return formatCategoryTitle(target);
  };

  const categoryIdToTitle = (id: string) => {
    if (!data || id === '') {
      return '';
    }

    const target = findRecursive(data, (item) => item.id === id);

    if (!target) {
      throw new Error(`Can't get category by id:${id}`);
    }

    return formatCategoryTitle(target);
  };

  const findCategoryById = (id: string) => {
    if (!data || id === '') {
      return undefined;
    }

    return findRecursive(data, (item) => item.id === id);
  };

  const displayNameToId = (displayName: DisplayName) => {
    if (!data) return undefined;

    return displayNameToIdBase(data, displayName);
  };

  return {
    isReady,
    categoryIdToTitle,
    findCategoryById,
    displayNameToId,
    displayNameToTitle,
    formatCategoryTitle,
  };
};
